import { z } from 'zod';

/** Shared shape for the format check; hoisted so the refine below reuses it. */
const emailFormat = z.string().email('Invalid email format');

/**
 * Email validator with nullable support
 * Validates email format, and allows null or '' for an account with no email.
 *
 * USERS.email is NOT NULL, so a phone-only signup — about a quarter of new
 * accounts — stores '' rather than null. Rejecting '' made GET /api/user/profile
 * throw on its own response contract and answer 500, which is what the settings
 * page saw for those users. `phoneValidator` below has always allowed '' for the
 * mirror-image reason; this matches it.
 */
export const emailValidator = z
  .string()
  .refine((val) => val === '' || emailFormat.safeParse(val).success, 'Invalid email format')
  .nullable();

/**
 * Email validator (required, non-nullable)
 * Validates email format
 */
export const emailValidatorRequired = z
  .string()
  .email('Invalid email format');

/**
 * Phone validator with international format support
 * Accepts formats like: +1234567890, (123) 456-7890, 123-456-7890
 * Allows null values and empty strings
 */
export const phoneValidator = z
  .string()
  .refine(
    (val) => val === '' || /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/.test(val),
    'Invalid phone number format'
  )
  .nullable();

/**
 * Phone validator (required, non-nullable)
 */
export const phoneValidatorRequired = z
  .string()
  .regex(
    /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/,
    'Invalid phone number format'
  );

/**
 * BigInt to number transformer
 * Handles conversion from BigInt, number, or string to number
 * Useful for database ID fields that may be returned as BigInt
 */
export const bigIntToNumber = z.union([
  z.number(),
  z.bigint().transform((val) => Number(val)),
  z.string().regex(/^\d+$/, 'Invalid number format').transform((val) => Number(val)),
]);

/**
 * BigInt to number transformer (nullable)
 */
export const bigIntToNumberNullable = z
  .union([
    z.number(),
    z.bigint().transform((val) => Number(val)),
    z.string().regex(/^\d+$/, 'Invalid number format').transform((val) => Number(val)),
  ])
  .nullable();

/**
 * Price validator
 * Accepts string or number and transforms to number
 * Ensures the value is non-negative
 */
export const priceValidator = z
  .union([
    z.number().nonnegative('Price must be non-negative'),
    z.string().transform((val) => {
      const num = parseFloat(val);
      if (isNaN(num)) {
        throw new Error('Invalid price format');
      }
      if (num < 0) {
        throw new Error('Price must be non-negative');
      }
      return num;
    }),
  ])
  .transform((val) => (typeof val === 'string' ? parseFloat(val) : val));

/**
 * Price validator (nullable)
 * Accepts: string decimals, numbers, Prisma Decimal objects, or null
 * Returns: number or null
 * Rejects: Infinity, -Infinity, NaN
 */
export const priceValidatorNullable = z
  .preprocess(
    (val: unknown) => {
      // Handle Prisma Decimal objects by converting them to numbers first
      const decimal = val as { toNumber?: () => number } | null;
      if (decimal && typeof decimal === 'object' && typeof decimal.toNumber === 'function') {
        return decimal.toNumber();
      }
      return val;
    },
    z.union([
      z.number()
        .refine((val) => Number.isFinite(val), 'Price must be a finite number')
        .refine((val) => val >= 0, 'Price must be non-negative'),
      z.string().regex(/^\d+(\.\d+)?$/, 'Price must be a valid decimal number').transform(val => parseFloat(val)),
      z.null(),
    ])
  );

/**
 * Coordinate validator for latitude/longitude
 * Validates number is between -180 and 180
 * Allows null values
 */
export const coordinateValidator = z
  .number()
  .min(-180, 'Coordinate must be between -180 and 180')
  .max(180, 'Coordinate must be between -180 and 180')
  .nullable();

/**
 * Coordinate validator (required, non-nullable)
 */
export const coordinateValidatorRequired = z
  .number()
  .min(-180, 'Coordinate must be between -180 and 180')
  .max(180, 'Coordinate must be between -180 and 180');

/**
 * Latitude validator (more specific range)
 * Validates number is between -90 and 90
 */
export const latitudeValidator = z
  .number()
  .min(-90, 'Latitude must be between -90 and 90')
  .max(90, 'Latitude must be between -90 and 90')
  .nullable();

/**
 * Latitude validator (required, non-nullable)
 */
export const latitudeValidatorRequired = z
  .number()
  .min(-90, 'Latitude must be between -90 and 90')
  .max(90, 'Latitude must be between -90 and 90');

/**
 * Longitude validator (more specific range)
 * Validates number is between -180 and 180
 */
export const longitudeValidator = z
  .number()
  .min(-180, 'Longitude must be between -180 and 180')
  .max(180, 'Longitude must be between -180 and 180')
  .nullable();

/**
 * Longitude validator (required, non-nullable)
 */
export const longitudeValidatorRequired = z
  .number()
  .min(-180, 'Longitude must be between -180 and 180')
  .max(180, 'Longitude must be between -180 and 180');

/**
 * Date validator using Zod's coercion
 * Accepts Date, string, or number and coerces to Date
 */
export const dateValidator = z.coerce.date();

/**
 * Date validator (nullable)
 */
export const dateValidatorNullable = z.coerce.date().nullable();

/**
 * URL validator
 * Validates URL format and allows null values
 */
export const urlValidator = z.string().url('Invalid URL format').nullable();

/**
 * URL validator (required, non-nullable)
 */
export const urlValidatorRequired = z.string().url('Invalid URL format');

/**
 * Positive integer validator
 * Validates the value is a positive integer
 */
export const positiveIntValidator = z
  .number()
  .int('Must be an integer')
  .positive('Must be positive');

/**
 * Positive integer validator (nullable)
 */
export const positiveIntValidatorNullable = z
  .number()
  .int('Must be an integer')
  .positive('Must be positive')
  .nullable();

/**
 * Non-negative integer validator (allows 0)
 */
export const nonNegativeIntValidator = z
  .number()
  .int('Must be an integer')
  .nonnegative('Must be non-negative');

/**
 * Non-negative integer validator (nullable)
 */
export const nonNegativeIntValidatorNullable = z
  .number()
  .int('Must be an integer')
  .nonnegative('Must be non-negative')
  .nullable();

/**
 * UUID validator
 * Validates UUID v4 format
 */
export const uuidValidator = z
  .string()
  .uuid('Invalid UUID format')
  .nullable();

/**
 * UUID validator (required, non-nullable)
 */
export const uuidValidatorRequired = z.string().uuid('Invalid UUID format');

/**
 * ISO date string validator
 * Validates ISO 8601 date string format
 */
export const isoDateStringValidator = z
  .string()
  .datetime('Invalid ISO date format')
  .nullable();

/**
 * ISO date string validator (required, non-nullable)
 */
export const isoDateStringValidatorRequired = z
  .string()
  .datetime('Invalid ISO date format');

// ============================================================================
// REQUEST VALIDATORS (with coercion for form/query string inputs)
// ============================================================================

/**
 * ID validator for request params (coerces string to positive int)
 * Use for: userId, listingId, categoryId, countryId, etc. in requests
 */
export const idValidator = z.coerce.number().int().positive();
export const idValidatorOptional = z.coerce.number().int().positive().optional();
export const idValidatorNullable = z.coerce.number().int().positive().nullable();

/**
 * Non-negative ID validator (allows 0)
 * Use for: status codes, flags, etc.
 */
export const idValidatorNonNegative = z.coerce.number().int().nonnegative();
export const idValidatorNonNegativeOptional = z.coerce.number().int().nonnegative().optional();

/**
 * Count/quantity validator (for page numbers, limits, etc.)
 */
export const countValidator = z.coerce.number().int().nonnegative();
export const countValidatorOptional = z.coerce.number().int().nonnegative().optional();

/**
 * Price validator with coercion (for form inputs)
 * Coerces strings to numbers and validates non-negative
 */
export const priceValidatorCoerce = z.coerce.number().nonnegative();
export const priceValidatorCoerceOptional = z.coerce.number().nonnegative().optional();

/**
 * Latitude validator with coercion (for form inputs)
 */
export const latitudeValidatorCoerce = z.coerce.number().min(-90).max(90);
export const latitudeValidatorCoerceOptional = z.coerce.number().min(-90).max(90).optional();
export const latitudeValidatorCoerceNullable = z.coerce.number().min(-90).max(90).nullable();

/**
 * Longitude validator with coercion (for form inputs)
 */
export const longitudeValidatorCoerce = z.coerce.number().min(-180).max(180);
export const longitudeValidatorCoerceOptional = z.coerce.number().min(-180).max(180).optional();
export const longitudeValidatorCoerceNullable = z.coerce.number().min(-180).max(180).nullable();
